import { logEvent } from "@src/appV2/lib/analytics";
import { useRecentlyVisited } from "@src/appV2/lib/utils/useRecentlyVisited";
import { ADMIN_EVENTS } from "@src/appV2/redesign/assets/constants/firebaseEvents";
import { useSearchAgents } from "@src/appV2/User/api/useSearchAgents";
import { useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { debounce, type DebouncedFunc } from "lodash";
import { useState } from "react";

import { AccountSwitcherUser, type UserDetails } from "./types";
import { useHistory } from "react-router-dom";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { getWorkerStageLabel, isWorkerStageEnum } from "@src/appV2/redesign/utils/worker";
import { useDispatch } from "react-redux";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { pseudo } from "@src/modules/session";

export const AGENTS_LIMIT_FOR_LOAD = 3;
export const FACILITIES_LIMIT_FOR_LOAD = 3;

function getStageLabel(stage: string): string {
  if (isWorkerStageEnum(stage)) {
    return getWorkerStageLabel(stage).toUpperCase();
  }

  return stage.toUpperCase();
}

export function useAccountSwitcher(): {
  switcherTitle: string;
  switcherSubtitle?: string;
  displayAdminSwitcher: boolean;
  dropdownVisible: boolean;
  setDropdownVisible: (visible: boolean) => void;
  onDropdownButtonClicked: () => void;
  workerDetails: UserDetails;
  workplaceDetails: UserDetails;
  onSearchChanged: DebouncedFunc<(name: string) => void>;
  onUserClicked: (user: AccountSwitcherUser) => void;
  onAdminClicked: () => void;
} {
  const session = useSession();
  const { profile, adminUserId, admin, adminProfile, type, user } = session;

  const facilityName = profile.name;
  const userName = user?.name;
  const adminName = adminProfile?.name;
  const isFacilityUser = type === "FACILITY";

  // Determine title and subtitle based on user type and context
  const { switcherTitle, switcherSubtitle } = (() => {
    if (admin) {
      return isFacilityUser && facilityName
        ? {
            // Admin viewing a facility
            switcherTitle: facilityName,
            switcherSubtitle: adminName ?? "Administrator",
          }
        : {
            // Admin not viewing a facility
            switcherTitle: adminName ?? "Select Account",
            switcherSubtitle: "Administrator",
          };
    }

    if (isFacilityUser) {
      return facilityName
        ? {
            // Non-admin facility user
            switcherTitle: facilityName,
            switcherSubtitle: userName,
          }
        : {
            // No facility selected (shouldn't happen for non-admin)
            switcherTitle: userName ?? "Facility",
            switcherSubtitle: undefined,
          };
    }

    // Fallback
    return {
      switcherTitle: "Select Account",
      switcherSubtitle: undefined,
    };
  })();

  const displayAdminSwitcher = admin;

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const onDropdownButtonClicked = () => {
    if (!dropdownVisible) {
      logEvent(ADMIN_EVENTS.OPEN_SEARCH);
    }
    setDropdownVisible(!dropdownVisible);
  };

  const [searchName, setSearchName] = useState<string>();
  const {
    data: searchUsersResponse,
    isLoading: isSearchUsersLoading,
    fetchNextPage: fetchNextPageSearchUsers,
    fetchStatus: fetchStatusSearchUsers,
    isFetchingNextPage: isFetchingNextPageSearchUsers,
    hasNextPage: hasNextPageSearchUsers,
  } = useSearchAgents({
    searchInput: searchName,
    limit: AGENTS_LIMIT_FOR_LOAD,
  });

  const workerDetails: UserDetails = {
    list:
      searchUsersResponse?.pages.flatMap((page) =>
        page.list.map((user) => ({
          type: "AGENT",
          userId: user.userId,
          name: user.name,
          title: `${user.name} · ${user.email}`,
          subtitles: [`${user.phone} · ${user._id}`],
          stage: getStageLabel(user.stage),
        }))
      ) ?? [],
    loading: isSearchUsersLoading && fetchStatusSearchUsers !== "idle",
    fetchNextPage: fetchNextPageSearchUsers,
    isFetchingNextPage: isFetchingNextPageSearchUsers,
    hasNextPage: hasNextPageSearchUsers,
  };

  const {
    data: searchFacilitiesResponse,
    isLoading: isSearchFacilitiesLoading,
    fetchNextPage: fetchNextPageSearchFacilities,
    fetchStatus: fetchStatusSearchFacilities,
    isFetchingNextPage: isFetchingNextPageSearchFacilities,
    hasNextPage: hasNextPageSearchFacilities,
  } = useSearchFacilities({
    searchInput: searchName ?? "",
    limit: AGENTS_LIMIT_FOR_LOAD,
  });

  const workplaceDetails: UserDetails = {
    list:
      searchFacilitiesResponse?.pages.flatMap((page) =>
        page.list.map((user) => ({
          type: "WORKPLACE",
          userId: user.userId,
          name: user.name,
          title: user.name,
          subtitles: [`${user._id} · ${user.pricingMsa}`],
          stage: getStageLabel(user.status),
        }))
      ) ?? [],
    loading: isSearchFacilitiesLoading && fetchStatusSearchFacilities !== "idle",
    fetchNextPage: fetchNextPageSearchFacilities,
    isFetchingNextPage: isFetchingNextPageSearchFacilities,
    hasNextPage: hasNextPageSearchFacilities,
  };

  const onSearchChanged = debounce((name: string) => {
    setSearchName(name);
    logEvent(ADMIN_EVENTS.FILTER_SEARCH, { user_input: name });
  }, 800);

  const [, addToRecent] = useRecentlyVisited();
  const history = useHistory();
  const dispatch = useDispatch();
  const handlePseudo = (data: { userId: string }, isFirebaseSession?: boolean) => {
    return dispatch(pseudo(data, isFirebaseSession));
  };
  async function switchUser(userId: string) {
    setDropdownVisible(false);

    // Wait for the session to update before reloading
    await handlePseudo({ userId });
    await history.push(getLocation("enter"));

    // Reload the page to ensure all components re-render with new session
    await window.location.reload();
  }
  function onUserClicked(user: AccountSwitcherUser) {
    const { userId, name, type } = user;

    addToRecent({ id: userId, title: name, type });

    if (type === "AGENT") {
      logEvent(ADMIN_EVENTS.SELECT_HCP, { id: userId });
    } else if (type === "WORKPLACE") {
      logEvent(ADMIN_EVENTS.SELECT_HCF, { id: userId });
    }

    switchUser(userId);
  }
  function onAdminClicked() {
    if (adminUserId) {
      switchUser(adminUserId);
    }
  }

  return {
    switcherTitle,
    switcherSubtitle,
    displayAdminSwitcher,
    dropdownVisible,
    setDropdownVisible,
    onDropdownButtonClicked,
    workerDetails,
    workplaceDetails,
    onSearchChanged,
    onUserClicked,
    onAdminClicked,
  };
}
